import { format, parse } from 'date-fns';
import {
  type CharityDocument,
  type DocumentFormValues,
  type DocumentTypeValue,
  type UploadSource,
} from 'features/documents/types';

import {
  type DocumentContext,
  type DocumentItem,
  type UpdateDocumentRequest,
  type UploadDocumentMetadata,
  type UploaderKind,
} from './types';

const UPLOAD_SOURCE_BY_UPLOADER_KIND: Record<UploaderKind, UploadSource> = {
  USER: 'user',
  SYSTEM: 'system',
};

export const toCharityDocument = (item: DocumentItem): CharityDocument => ({
  id: item.id,
  name: item.name,
  chainName: item.chain?.name ?? null,
  storeNames: item.stores.map((store) => store.name),
  type: item.documentType,
  uploadedAt: item.uploadedAt,
  uploadedBy: UPLOAD_SOURCE_BY_UPLOADER_KIND[item.uploader.kind],
  uploadedByName: item.uploader.displayName,
  period: item.period,
  dates: item.dates ?? null,
  storeId: item.stores[0]?.id ?? null,
  // without the raw dates an update would erase them, as it replaces every field
  isEditable: item.isEditable && item.dates !== undefined,
  isDeletable: item.isDeletable,
});

const MONTH_FORMAT = 'yyyy-MM';

const toMonth = (date?: Date | null) => (date ? format(date, MONTH_FORMAT) : undefined);

const toMonthDate = (month?: string | null) =>
  month ? parse(month, MONTH_FORMAT, new Date()) : undefined;

export const toDocumentFormDefaults = (
  document: CharityDocument,
): Omit<DocumentFormValues, 'name'> => ({
  documentType: document.type,
  storeId: document.storeId,
  periodFrom: toMonthDate(document.dates?.periodFrom),
  periodTo: toMonthDate(document.dates?.periodTo),
  validFrom: document.dates?.validFrom ?? undefined,
  validTo: document.dates?.validTo ?? undefined,
});

export const toUploadDocumentMetadata = (
  context: DocumentContext,
  documentType: DocumentTypeValue,
  values: DocumentFormValues,
): UploadDocumentMetadata => ({
  context,
  documentType,
  documentName: values.name,
  storeId: values.storeId ?? undefined,
  periodFrom: toMonth(values.periodFrom),
  periodTo: toMonth(values.periodTo),
  validFrom: values.validFrom || undefined,
  validTo: values.validTo || undefined,
});

// the update replaces every field, so fields the new type doesn't take are sent as null
export const toUpdateDocumentRequest = (
  context: DocumentContext,
  document: CharityDocument,
  documentType: DocumentTypeValue,
  values: DocumentFormValues,
): UpdateDocumentRequest => ({
  context,
  documentId: document.id,
  documentName: values.name,
  documentType,
  period: document.dates?.period ?? null,
  periodFrom: toMonth(values.periodFrom) ?? null,
  periodTo: toMonth(values.periodTo) ?? null,
  validFrom: values.validFrom || null,
  validTo: values.validTo || null,
  storeId: values.storeId ?? null,
  changeNote: values.changeNote?.trim() ?? '',
});
